import { Link } from 'react-router-dom';
import GuideLayout from '../../components/guides/GuideLayout';
import GuideCTA from '../../components/guides/GuideCTA';
import { GuideSection, GuideFaqItem, GuideChecklist, GuideNote, SourceLink } from '../../components/guides/GuideParts';

const SLUG = 'what-is-a-subject-access-request';

export const WhatIsASar = () => (
  <GuideLayout slug={SLUG}>
    <GuideSection>
      <p>
        A Subject Access Request, usually shortened to <strong className="text-ink">SAR</strong>,
        is a formal request that makes an organisation show you every piece of personal
        information it holds about you. It is not a favour and not something you need
        permission to do. It is a legal right, set out in{' '}
        <SourceLink href="https://www.legislation.gov.uk/eur/2016/679/article/15">
          Article 15 of the UK GDPR
        </SourceLink>{' '}
        and the{' '}
        <SourceLink href="https://www.legislation.gov.uk/ukpga/2018/12/contents">
          Data Protection Act 2018
        </SourceLink>
        , and it applies to almost anyone who has ever held a file on you: a council, a school,
        a GP surgery, a hospital, a fostering agency, the police.
      </p>
      <p>
        For anyone who grew up in care or was adopted, this matters more than most. Decisions
        were made about your life, meetings were held about you, and reports were written about
        you, often by people you barely remember and rarely saw. A SAR is how you get to read
        what they wrote.
      </p>
    </GuideSection>

    <GuideSection eyebrow="What you can ask for" title="What a SAR actually gets you">
      <p>Once an organisation accepts your request, it has to give you:</p>
      <GuideChecklist
        items={[
          'A copy of the personal data itself — case notes, letters, emails about you, assessments, meeting minutes, photographs, anything that identifies you.',
          'Confirmation of why they are processing your data and their legal basis for doing so.',
          'Who else they have shared your data with, or might share it with.',
          'How long they intend to keep it, or the criteria they use to decide.',
          'Where the information came from, if it was not collected directly from you.',
        ]}
      />
      <p>
        This is a right to the underlying record, not a summary written for the occasion. An
        organisation cannot satisfy a SAR with a short letter saying "you were in our care from
        2009 to 2014" when it holds hundreds of pages of case notes from that period.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Deadlines" title="How long they have to respond">
      <p>
        By law, an organisation must respond{' '}
        <strong className="text-ink">within one calendar month</strong> of receiving a valid
        request. For a genuinely complex request, or where someone has made several requests,
        they can extend this by up to two further months — but they have to tell you within the
        first month that they are doing so, and explain why.
      </p>
      <GuideNote>
        <p>
          <strong>In practice:</strong> requesting records from your entire childhood, across
          several placements and possibly several organisations, is exactly the kind of thing
          that can count as complex. An extension is not automatically unreasonable. What is
          unreasonable is silence, or an extension with no explanation.
        </p>
      </GuideNote>
    </GuideSection>

    <GuideSection eyebrow="Identity checks" title="Can they ask for ID first?">
      <p>
        Yes, but only what is reasonable. An organisation is entitled to confirm it is really
        you before handing over sensitive information — that protects you as much as it protects
        them. What it cannot do is use ID checks as a way to slow you down: demanding documents
        you cannot easily get hold of, or restarting the one-month clock every time it thinks of
        another thing to ask for.
      </p>
      <p>
        If you are asking on behalf of a child still in care, or as someone helping an adult
        care leaver, you may also need to show your own authority to act — a support worker
        letter, power of attorney, or similar, depending on the situation.
      </p>
    </GuideSection>

    <GuideCTA />

    <GuideSection eyebrow="Cost" title="Does it cost anything?">
      <p>
        No. The right to make a SAR is free. An organisation can only charge a "reasonable fee"
        if your request is manifestly unfounded or excessive — for example, if you ask for the
        same records repeatedly with no new reason — or for extra copies beyond the first. A
        standard request for your own care, school, health or police records does not fall into
        either category.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Common questions" title="Questions people ask us most" tinted>
      <div className="space-y-2" data-testid="what-is-a-sar-faq">
        <GuideFaqItem
          q="Do I need a solicitor to make a SAR?"
          a="No. Anyone can write and send one themselves — there's no special form and no fee. Precious Data exists because doing this for every organisation that might hold something is a lot of admin, not because it's legally complicated."
        />
        <GuideFaqItem
          q="What if I don't remember the exact council or agency?"
          a="You don't need the exact name. A town, an approximate age, or the name of a school or foster carer is often enough to work out who to ask, and organisations will search under names and spellings you give them."
        />
        <GuideFaqItem
          q="Can I request records from when I was a child, now that I'm an adult?"
          a="Yes. Your right of access doesn't expire, and most care-related records have long statutory retention periods, often until you turn 75. If a specific organisation says it no longer holds anything, it should tell you when the records were destroyed and under what policy."
        />
        <GuideFaqItem
          q="Is a SAR the same as a Freedom of Information request?"
          a={
            <>
              No, and it matters — see our guide on{' '}
              <Link
                to="/guides/freedom-of-information-vs-subject-access-request"
                className="text-brand underline hover:opacity-90"
              >
                Freedom of Information vs a Subject Access Request
              </Link>
              . In short: FOI is about information a public body holds generally — policies,
              statistics, decisions. A SAR is specifically about information held about you as an
              individual, and only you (or someone you authorise) can make one for your own data.
            </>
          }
        />
        <GuideFaqItem
          q="What happens if they ignore me?"
          a="You can complain to the Information Commissioner's Office (ICO), the UK regulator for data protection, who can investigate and require the organisation to comply. See our guide on what to do if an organisation misses the deadline."
        />
      </div>
    </GuideSection>

    <GuideSection eyebrow="Read more" title="Where this comes from">
      <p>
        The full legal detail is set out by the ICO's own guidance on the{' '}
        <SourceLink href="https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/individual-rights/right-of-access/">
          right of access
        </SourceLink>
        , and its plain-English page on{' '}
        <SourceLink href="https://ico.org.uk/for-the-public/what-rights-do-i-have-over-my-personal-information/">
          what rights you have over your personal information
        </SourceLink>
        . The ICO also runs a dedicated page for care leavers and care-experienced people trying
        to piece together their own history, called{' '}
        <SourceLink href="https://ico.org.uk/about-the-ico/campaigns/better-records-together/">
          Better Records Together
        </SourceLink>
        .
      </p>
    </GuideSection>

    <GuideCTA
      heading="Ready to see what's held about you?"
      body="Add the organisations you remember — a council, a school, a foster agency — and we'll draft the letters, track the deadlines, and keep everything in one place as it comes back."
      testIdPrefix="guide-cta-end"
    />
  </GuideLayout>
);

export default WhatIsASar;
